function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { requiredValidation } from 'validators';
import { ConfirmationModal, MenuItem, Spinner, TextField, Typography } from 'seagull';
import RestfulWrapper from "../../../components/RestfulWrapper/RestfulWrapper";
import { useGlobalContext, useStore, useStoreActions } from "../../../contexts";
import { connectControllerToTextField } from "../../../utils/react-hook-form.utils";
import { memberModel } from "../../../models/member";
import { selectors } from "../../../store/state-selectors";
import styles from "./RemoveFromTeamModal.module.scss";
import { UserRole } from "../../../store/store.model";
const commonModalProps = {
  title: 'Remove team member',
  skin: 'danger',
  primaryButtonLabel: 'Remove team member',
  secondaryButtonLabel: 'Cancel'
};
export default function RemoveFromTeamModal(props) {
  const {
    member,
    dismissModal
  } = props;
  const {
    api
  } = useStoreActions();
  const onClose = () => dismissModal();
  return /*#__PURE__*/React.createElement(RestfulWrapper, {
    fetch: () => api.member.getAssignedRelationships(member.id).then(res => res.data),
    renderChild: _ref => {
      let {
        data
      } = _ref;
      return /*#__PURE__*/React.createElement(RemoveFromTeamModalWithData, _extends({}, props, {
        numAssignedRelationships: data.count
      }));
    },
    renderLoader: () => /*#__PURE__*/React.createElement(ConfirmationModal, _extends({}, commonModalProps, {
      content: /*#__PURE__*/React.createElement(Spinner, null),
      primaryButtonDisabled: true,
      onCloseButtonClick: onClose,
      onSecondaryButtonClick: onClose
    }))
  });
}
function RemoveFromTeamModalWithData(props) {
  const {
    member,
    allMembers,
    numAssignedRelationships,
    onRefresh,
    dismissModal
  } = props;
  const isVendor = useStore(selectors.user.isVendor);
  const {
    api,
    logBI
  } = useStoreActions();
  const globalContext = useGlobalContext();
  const {
    control,
    handleSubmit
  } = useForm({
    mode: 'all',
    defaultValues: {
      memberIdToReassign: ''
    }
  });
  const hasAssignedRelationships = !!numAssignedRelationships;
  const onClose = () => dismissModal();
  const optionsToReassign = useMemo(() => {
    return allMembers.filter(m => m.id !== member.id && m.role !== 'invited' && (isVendor || m.role !== UserRole.Member)).map(m => ({
      id: m.id,
      text: "".concat(m.firstName, " ").concat(m.lastName)
    }));
  }, [allMembers, member, isVendor]);
  const onRemove = async _ref2 => {
    let {
      memberIdToReassign
    } = _ref2;
    const onRemoveLoader = 'rmMember';
    globalContext.startLoader(onRemoveLoader);
    try {
      if (hasAssignedRelationships) {
        await api.member.reassignAllRelationships({
          assignedUser: member.id,
          assignToUser: memberIdToReassign
        });
      }
      await api.member.remove({
        id: member.id,
        currentRole: memberModel.effectiveRole(member)
      });
      logBI(12203, {
        id: member.id,
        email: member.email,
        reassignTo: memberIdToReassign
      });
    } catch (err) {
      globalContext.showErrorToast('Failed to remove team member', 'rm-member-fail');
      return globalContext.stopLoader(onRemoveLoader);
    }
    try {
      await onRefresh();
    } finally {
      globalContext.stopLoader(onRemoveLoader);
      dismissModal();
    }
  };
  return /*#__PURE__*/React.createElement(ConfirmationModal, _extends({}, commonModalProps, {
    "data-testid": "rm-member",
    textAlignCenter: false,
    onCloseButtonClick: onClose,
    onSecondaryButtonClick: onClose,
    onPrimaryButtonClick: handleSubmit(onRemove),
    content: hasAssignedRelationships ? /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Typography, {
      className: styles.paragraph
    }, "In order to remove", ' ', /*#__PURE__*/React.createElement(Typography, {
      as: "span",
      weight: "normal"
    }, member.firstName, " ", member.lastName), ", you need to select another team member to reassign all proposals and agreements under their name."), /*#__PURE__*/React.createElement(Typography, {
      className: styles.paragraph
    }, "Please select one from the list"), /*#__PURE__*/React.createElement(Controller, {
      name: "memberIdToReassign",
      control: control,
      rules: requiredValidation(),
      render: p => /*#__PURE__*/React.createElement(TextField, _extends({
        select: true,
        className: styles.paragraph,
        width: 280,
        testid: "rm-member-reassign",
        label: "Select team member"
      }, connectControllerToTextField(p)), optionsToReassign.map(_ref3 => {
        let {
          id,
          text
        } = _ref3;
        return /*#__PURE__*/React.createElement(MenuItem, {
          key: id,
          value: id,
          "data-testid": "rm-member-reassign-opt-".concat(id)
        }, text);
      }))
    })) : /*#__PURE__*/React.createElement(Typography, null, "Are you sure you want to remove", ' ', /*#__PURE__*/React.createElement(Typography, {
      as: "span",
      weight: "normal"
    }, member.firstName, " ", member.lastName), ' ', "from your team? Once removed they will no longer be able to access your business.")
  }));
}